import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Header from "./Header";
import Container from "@mui/material/Container";
import { Divider, Grid } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AboutMeSec from "./AboutMeSec";
import { useLoading } from "../contexts/DisplayLoading";

import "../css/aboutMe.css";
import AboutMeImage from "../images/Taimmmm.webp";
// Animation Variants
const fader = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, delayChildren: 0.3 },
  },
};

export default function AboutMe() {
  const { loading, DisplayLoading } = useLoading();
  const { scrollYProgress } = useScroll();

  const yImage = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const rotateImage = useTransform(scrollYProgress, [0, 1], [0, 5]);

  DisplayLoading();

  return (
    <div
      className="about-me-page"
      style={{
        backgroundColor: loading.isVisible ? "black" : "white",
        color: "#111",
        overflowX: "hidden",
      }}
    >
      {!loading.isVisible && (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <Header
            headerColor="black"
            hoverColor="black"
            shadowColor="transparent"
          />

          {/* --- HERO SECTION --- */}
          <Container maxWidth="lg" sx={{ padding: "120px 20px 60px" }}>
            <motion.div variants={fader}>
              <h1
                style={{
                  fontSize: "clamp(40px, 8vw, 85px)",
                  fontWeight: "800",
                  lineHeight: "1",
                }}
              >
                <AutoAwesomeIcon
                  sx={{ fontSize: "40px", verticalAlign: "middle", mr: 2 }}
                />
                ELEVATING
              </h1>

              <h1
                className="headline"
                style={{
                  fontSize: "clamp(40px, 8vw, 85px)",
                  fontWeight: "800",
                  lineHeight: "1",
                  paddingLeft: "15%",
                  color: "#455CE9",
                }}
              >
                DIGITAL BRANDS
              </h1>

              <h1
                className="headline"
                style={{
                  fontSize: "clamp(40px, 8vw, 85px)",
                  fontWeight: "800",
                  lineHeight: "1",
                  paddingLeft: "30%",
                }}
              >
                TO NEW DEPTHS
                <AutoAwesomeIcon
                  sx={{ fontSize: "40px", verticalAlign: "middle", ml: 2 }}
                />
              </h1>
            </motion.div>
          </Container>

          <Divider
            sx={{ borderColor: "rgba(0,0,0,0.1)", width: "90%", mx: "auto" }}
          />

          {/* --- INTRO SECTION --- */}
          <Container maxWidth="lg" sx={{ padding: "100px 20px" }}>
            <Grid container spacing={8}>
              <Grid item xs={12} md={6}>
                <motion.div variants={fader}>
                  <p
                    style={{
                      fontSize: "clamp(24px, 3vw, 38px)",
                      fontWeight: "800",
                      textTransform: "uppercase",
                    }}
                  >
                    Every line of code should{" "}
                    <span style={{ color: "#455CE9" }}>breathe</span> life into
                    a vision.
                  </p>

                  <p
                    style={{
                      fontSize: "18px",
                      lineHeight: "1.8",
                      color: "#444",
                    }}
                  >
                    I am a Creative Technologist focused on the intersection of
                    design and motion.
                  </p>

                  <p
                    style={{
                      fontSize: "18px",
                      lineHeight: "1.8",
                      color: "#444",
                      marginTop: "20px",
                    }}
                  >
                    I create digital experiences that make your brand not just
                    seen — but felt.
                  </p>
                </motion.div>
              </Grid>

              <Grid item xs={12} md={6}>
                <motion.div style={{ y: yImage, rotate: rotateImage }}>
                  <img
                    src={AboutMeImage}
                    alt="Taim Portfolio"
                    style={{
                      width: "100%",
                      borderRadius: "20px",
                      boxShadow: "0 50px 80px rgba(0,0,0,0.15)",
                    }}
                  />

                  <motion.div
                    animate={{ y: [0, -15, 0] }}
                    transition={{ repeat: Infinity, duration: 4 }}
                    style={{
                      position: "absolute",
                      bottom: "-20px",
                      right: "-20px",
                      background: "#455CE9",
                      color: "#fff",
                      padding: "20px",
                      borderRadius: "50%",
                      width: "120px",
                      height: "120px",
                      display: "flex",
                      alignItems: "center",
                      textAlign: "center",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    EST. 2024 CREATIVE DEV
                  </motion.div>
                </motion.div>
              </Grid>
            </Grid>
          </Container>

          {/* --- WHY WORK WITH ME (LIGHT VERSION) --- */}
          <div
            style={{
              backgroundColor: "#F7F8FA",
              color: "#111",
              padding: "110px 0",
              borderRadius: "40px 40px 0 0",
            }}
          >
            <Container maxWidth="lg">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2
                  style={{
                    fontSize: "clamp(32px, 5vw, 60px)",
                    marginBottom: "70px",
                    fontWeight: "800",
                  }}
                >
                  Why Work With Me?
                </h2>

                <Grid container spacing={5}>
                  {[
                    {
                      title: "Motion First",
                      desc: "Animation is baked into the experience — not sprinkled on top.",
                    },
                    {
                      title: "React Expertise",
                      desc: "Modern, scalable React architecture using best practices.",
                    },
                    {
                      title: "Brand Synergy",
                      desc: "Your brand translated into a living digital identity.",
                    },
                    {
                      title: "Purposeful Pixels",
                      desc: "Every element earns its place. No noise, just clarity.",
                    },
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <div
                        style={{
                          borderLeft: "2px solid #455CE9",
                          padding: "25px 30px",
                          height: "100%",
                          background: "#fff",
                          borderRadius: "12px",
                          boxShadow: "0 20px 40px rgba(0,0,0,0.04)",
                        }}
                      >
                        <h3
                          style={{
                            color: "#455CE9",
                            fontSize: "22px",
                            marginBottom: "12px",
                          }}
                        >
                          0{index + 1}. {item.title}
                        </h3>
                        <p
                          style={{
                            fontSize: "16px",
                            color: "#555",
                            lineHeight: "1.7",
                          }}
                        >
                          {item.desc}
                        </p>
                      </div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Container>
          </div>

          <AboutMeSec />
        </motion.div>
      )}
    </div>
  );
}
